import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	PuzzleFilter,
	GameMysteriousJigsawSeriesDTO,
	GameMysteriousJigsawSeriesDTORequest,
	GameMysteriousJigsawTreasureDTO,
	PublishDTO,
	TreasureFilter,
} from '../DTO/treasury/puzzle.interface';

export const puzzleApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getPuzzlePage: builder.query<
			SuccessResponse<Paginated<GameMysteriousJigsawSeriesDTO>>,
			PuzzleFilter
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['PuzzleList'],
		}),
		getPuzzleSortList: builder.query<
			SuccessResponse<GameMysteriousJigsawSeriesDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/game/mysterious-jigsaw/series/list`,
				method: 'GET',
			}),
		}),
		sortPuzzle: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleList'],
		}),
		createPuzzle: builder.mutation<
			SuccessResponse<any>,
			GameMysteriousJigsawSeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw`,
				method: 'POST',
				body,
			}),
		}),
		updatePuzzle: builder.mutation<
			SuccessResponse<any>,
			GameMysteriousJigsawSeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleDetail'],
		}),
		setPuzzleStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleList', 'PuzzleDetail'],
		}),
		getPuzzleDetail: builder.query<
			SuccessResponse<GameMysteriousJigsawSeriesDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/mysterious-jigsaw/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['PuzzleDetail'],
		}),
		getPuzzleTreasurePage: builder.query<
			SuccessResponse<Paginated<GameMysteriousJigsawTreasureDTO>>,
			TreasureFilter
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/series/${body.seriesId}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['PuzzleTreasureList'],
		}),
		getPuzzleTreasureSortList: builder.query<
			SuccessResponse<GameMysteriousJigsawTreasureDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/game/mysterious-jigsaw/series/${id}/list`,
				method: 'GET',
			}),
		}),
		sortPuzzleTreasure: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/treasure/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleTreasureList'],
		}),
		sortPuzzleTreasureLocation: builder.mutation<
			SuccessResponse<any>,
			string[]
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/treasure/layer-sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleTreasureList'],
		}),
		setPuzzleTreasureStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/treasure/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleTreasureList', 'PuzzleTreasureDetail'],
		}),
		deletePuzzle: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/mysterious-jigsaw/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getPuzzleTreasureDetail: builder.query<
			SuccessResponse<GameMysteriousJigsawTreasureDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/mysterious-jigsaw/treasure/${id}`,
				method: 'GET',
			}),
			providesTags: ['PuzzleTreasureDetail'],
		}),
		updatePuzzleTreasureDetail: builder.mutation<
			SuccessResponse<any>,
			GameMysteriousJigsawTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/treasure/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PuzzleTreasureDetail'],
		}),
		createPuzzleTreasure: builder.mutation<
			SuccessResponse<any>,
			GameMysteriousJigsawTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/mysterious-jigsaw/series/${body.puzzleId}`,
				method: 'POST',
				body,
			}),
		}),
		deletePuzzleTreasure: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/mysterious-jigsaw/treasure/${id}`,
				method: 'DELETE',
			}),
		}),
		getPuzzleTreasureLocationPage: builder.query<
			SuccessResponse<Paginated<GameMysteriousJigsawTreasureDTO>>,
			string
		>({
			query: (id) => ({
				url: `/web/game/mysterious-jigsaw/series/${id}/layer-sort`,
				method: 'GET',
			}),
		}),
	}),
});

export const {
	useGetPuzzlePageQuery,
	useGetPuzzleSortListQuery,
	useSortPuzzleMutation,
	useCreatePuzzleMutation,
	useUpdatePuzzleMutation,
	useSetPuzzleStatusMutation,
	useGetPuzzleDetailQuery,
	useGetPuzzleTreasurePageQuery,
	useGetPuzzleTreasureSortListQuery,
	useSortPuzzleTreasureMutation,
	useSetPuzzleTreasureStatusMutation,
	useDeletePuzzleMutation,
	useGetPuzzleTreasureDetailQuery,
	useUpdatePuzzleTreasureDetailMutation,
	useCreatePuzzleTreasureMutation,
	useDeletePuzzleTreasureMutation,
	useGetPuzzleTreasureLocationPageQuery,
	useSortPuzzleTreasureLocationMutation,
} = puzzleApiSlice;
